import React from 'react';
import { Box, ClickAwayListener, Menu, MenuItem as MuiMenuItem, SvgIcon, Typography } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { TabletButton } from '@campfire/tablet-button';
import { ExpandMore } from '@material-ui/icons';
import { useCampfireTheme } from '../../../theme/useCampfireTheme';
import { ReactComponent as LogoutIcon } from '../../../assets/logout-light.svg';
import { ReactComponent as AvailableIcon } from '../../../assets/check-square-light.svg';

export type RoleActionType = 'apply' | 'withdraw';

export type RoleDisplayStatus = 'available' | 'pending' | 'in_progress' | 'awaiting_review' | 'approved';

const ACTION_MAP = {
  apply: { text: 'Apply', icon: AvailableIcon },
  withdraw: { text: 'Withdraw', icon: LogoutIcon },
};

const STATUS_COLOR = new Map<RoleDisplayStatus, string>([
  ['available', '#A7D6FC'],
  ['pending', '#F4DA75'],
  ['in_progress', '#F4DA75'],
  ['awaiting_review', '#F4DA75'],
  ['approved', '#ABDC40'],
]);

const STATUS_LABEL = new Map<RoleDisplayStatus, string>([
  ['available', 'Available'],
  ['pending', 'Pending'],
  ['in_progress', 'In progress'],
  ['awaiting_review', 'Awaiting review'],
  ['approved', 'Joined'],
]);

export function getRoleStatusColor(status: RoleDisplayStatus): string {
  return STATUS_COLOR.get(status) ?? '#A7D6FC';
}

const useMenuItemStyles = makeStyles(() =>
  createStyles({
    container: {
      display: 'flex',
      alignItems: 'center',
    },
    icon: {
      width: '1rem',
      height: '1rem',
      marginRight: '1rem',
    },
  })
);

function ActionMenuItem({ action, onAction }: { action: RoleActionType; onAction: (action: RoleActionType) => void }) {
  const { text, icon: ActionIcon } = ACTION_MAP[action];
  const classes = useMenuItemStyles();
  const onClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onAction(action);
  };
  return (
    <MuiMenuItem onClick={onClick} className={classes.container}>
      <SvgIcon viewBox='0 0 16 16' fontSize='small' className={classes.icon}>
        <ActionIcon />
      </SvgIcon>
      <Typography>{text}</Typography>
    </MuiMenuItem>
  );
}

interface RoleStatusPillProps {
  status: RoleDisplayStatus;
}

function RoleStatusPill({ status }: RoleStatusPillProps) {
  const { theme } = useCampfireTheme();
  return (
    <Box display='flex' alignItems='center'>
      <Box height='10px' width='10px' borderRadius='50%' marginRight='8px' bgcolor={STATUS_COLOR.get(status)} />
      <Typography
        variant='caption'
        style={{
          fontSize: '12px',
          fontWeight: 700,
          color: theme.color.grey.neutral400,
          textTransform: 'uppercase',
          lineHeight: '14px',
          marginTop: '1px',
        }}
      >
        {STATUS_LABEL.get(status)}
      </Typography>
    </Box>
  );
}

interface Props {
  status: RoleDisplayStatus;
  actions: RoleActionType[];
  onAction: (action: RoleActionType) => void;
}

export function MyRoleStatusIndicator({ status, actions, onAction }: Props) {
  const { theme } = useCampfireTheme();
  const [anchorEl, setAnchorEl] = React.useState();

  const onOpenMenu = (event: any) => {
    if (actions.length > 0) {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    }
  };

  return (
    <ClickAwayListener onClickAway={() => setAnchorEl(undefined)}>
      <div>
        <TabletButton
          variant='outlined'
          color='primary'
          aria-controls='role-actions'
          aria-haspopup='true'
          onClick={onOpenMenu}
          endIcon={
            actions.length > 0 ? (
              <ExpandMore style={{ color: theme.color.grey.neutral300, marginLeft: '-2px' }} />
            ) : (
              undefined
            )
          }
          style={{ padding: '8px 12px', border: '1px solid #DCDCDC', whiteSpace: 'nowrap' }}
        >
          <RoleStatusPill status={status} />
        </TabletButton>
        <Menu
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          keepMounted
          onClose={(e: any) => {
            e.stopPropagation();
            setAnchorEl(undefined);
          }}
          getContentAnchorEl={null}
          PaperProps={{ style: { border: 'solid 1px #e9e9e9' } }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {actions.map((action) => (
            <ActionMenuItem key={action} action={action} onAction={onAction} />
          ))}
        </Menu>
      </div>
    </ClickAwayListener>
  );
}
